// ─── Auth: Supabase email magic link + profiles ────────────────────────────
// Sign-in is optional: anonymous posting keeps working exactly as before.
// Signed-in users get a profile row (display name, alert/confirm counts,
// night-owl flag) that powers badges and the "Maya · 24 alerts" trust line.
// Requires supabase-schema-v3.sql (profiles table) on top of v1 + v2.

import { useState, useEffect } from "react";
import { supabase, SUPABASE_READY } from "./supabase";

export const AUTH_READY = SUPABASE_READY;

// --- Session hook -----------------------------------------------------------
export function useSession() {
  const [session, setSession] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(!AUTH_READY);
  useEffect(() => {
    if (!AUTH_READY) return;
    let sub = null;
    (async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session || null);
      setAuthLoaded(true);
      const { data: listener } = supabase.auth.onAuthStateChange((_evt, s) => {
        setSession(s || null);
      });
      sub = listener;
    })();
    return () => {
      if (sub) sub.subscription.unsubscribe();
    };
  }, []);
  return { session, user: (session && session.user) || null, authLoaded };
}

// --- Magic link -------------------------------------------------------------
export async function sendMagicLink(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: { emailRedirectTo: window.location.origin },
  });
  if (error) throw error;
}

export async function signOut() {
  await supabase.auth.signOut();
}

// --- Profiles ---------------------------------------------------------------
const DEFAULT_NAME = "Neighbor";

export async function ensureProfile(user) {
  if (!AUTH_READY || !user) return null;
  const { data } = await supabase.from("profiles").select("*").eq("id", user.id).single();
  if (data) return data;
  const { data: created } = await supabase
    .from("profiles")
    .insert({ id: user.id, display_name: DEFAULT_NAME })
    .select()
    .single();
  return created || { id: user.id, display_name: DEFAULT_NAME, alert_count: 0, confirm_count: 0, night_owl_earned: false };
}

export async function getProfile(userId) {
  if (!AUTH_READY || !userId) return null;
  const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
  return data || null;
}

// Batch-fetch profiles for trust lines on the feed (public SELECT).
export async function getProfiles(userIds) {
  if (!AUTH_READY || !userIds.length) return {};
  const { data } = await supabase.from("profiles").select("id, display_name, alert_count").in("id", userIds);
  const map = {};
  for (const p of data || []) map[p.id] = p;
  return map;
}

export async function setDisplayName(userId, name) {
  const clean = String(name || "").trim().slice(0, 40) || DEFAULT_NAME;
  const { error } = await supabase.from("profiles").update({ display_name: clean }).eq("id", userId);
  if (error) throw error;
  return clean;
}

// Increment alert_count or confirm_count for the signed-in owner.
export async function bumpCount(userId, field) {
  if (!AUTH_READY || !userId) return;
  if (field !== "alert_count" && field !== "confirm_count") return;
  const { data } = await supabase.from("profiles").select("alert_count, confirm_count").eq("id", userId).single();
  const cur = (data && data[field]) || 0;
  await supabase.from("profiles").update({ [field]: cur + 1 }).eq("id", userId);
}

export async function markNightOwl(userId) {
  if (!AUTH_READY || !userId) return;
  await supabase.from("profiles").update({ night_owl_earned: true }).eq("id", userId);
}

// Estimated lbs rescued by this user's posted deals (reuses the app's math).
export async function rescuedByUser(userId) {
  if (!AUTH_READY || !userId) return 0;
  const { data } = await supabase.from("deals").select("est_lbs").eq("poster_id", userId);
  return (data || []).reduce((sum, d) => sum + (Number(d.est_lbs) || 0), 0);
}

export function isNightOwlNow(date = new Date()) {
  return date.getHours() >= 20; // 8pm or later local time
}
